// RunsList.js
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, orderBy, addDoc, arrayUnion, arrayRemove, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import { FaTrash, FaPencilAlt, FaSort, FaSortUp, FaSortDown, FaPlus, FaTimes, FaShareAlt } from "react-icons/fa";
import "../App.css";
import { signInAnonymously } from "firebase/auth";
import ShareGroupModal from "./ShareGroupModal";
import { getShareToken } from "../services/sharing";
import { getSharingGroupByToken, calculateSharingGroupScope } from "../services/sharingGroups";
import FilterDropdown from "./FilterDropdown";

const RunsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [runs, setRuns] = useState([]);
  const [sessions, setSessions] = useState(new Map());
  const [editingRunId, setEditingRunId] = useState(null);
  const [runNameEdit, setRunNameEdit] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [draggedRunId, setDraggedRunId] = useState(null);
  const [newSessionName, setNewSessionName] = useState("");
  const [showNewSessionInput, setShowNewSessionInput] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionName, setEditingSessionName] = useState("");
  const [editingRunTrack, setEditingRunTrack] = useState(null);
  const [runTrackEdit, setRunTrackEdit] = useState("");
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null); // 'session' or 'track' or null
  const [selectedSessionFilters, setSelectedSessionFilters] = useState([]); // Array of session IDs
  const [selectedTrackFilters, setSelectedTrackFilters] = useState([]); // Array of track names

  const [filtersRestored, setFiltersRestored] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  const shareGroupId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("id");
    if (value) {
      localStorage.setItem("guestShareGroupId", value);
      return value;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    return cached || "";
  }, [location.search]);

  const shareToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const shareGroupToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("shareGroupToken") || "";
  }, [location.search]);

  const [tokenData, setTokenData] = useState(null);
  const [tokenScope, setTokenScope] = useState(null);
  const [shareGroupData, setShareGroupData] = useState(null);
  const [shareGroupScope, setShareGroupScope] = useState(null);

  const isGuest = Boolean(user?.isAnonymous);
  const isGuestShare = Boolean(shareGroupId) && (isGuest || !user);
  const isTokenShare = Boolean(shareToken);
  const isShareGroupShare = Boolean(shareGroupToken);

  const requireAccount = () => {
    if (!user || user.isAnonymous) {
      showToast("Please sign in to edit or create content.");
      navigate("/login");
      return false;
    }
    return true;
  };

  const getFiltersStorageKey = () => {
    if (!user?.uid) return "runsListFilters:anonymous";
    return `runsListFilters:${user.uid}`;
  };

  const trackParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("track");
    return value ? value.trim() : "";
  }, [location.search]);

  useEffect(() => {
    if (!trackParam) return;
    setSelectedSessionFilters([]);
    setSelectedTrackFilters([trackParam]);
  }, [trackParam]);

  useEffect(() => {
    if ((!shareGroupId && !shareToken && !shareGroupToken) || user) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, shareToken, shareGroupToken, user]);

  // Load token data when token is present
  useEffect(() => {
    if (!shareToken) {
      setTokenData(null);
      setTokenScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareToken) {
      // Sign in anonymously if needed
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadToken = async () => {
      try {
        const data = await getShareToken(shareToken);
        if (data && !data.revoked) {
          setTokenData(data);
          setTokenScope(data.scope || { runs: [], racers: [], tracks: [], sessions: [], groups: [] });
        } else {
          setTokenData(null);
          setTokenScope(null);
        }
      } catch (error) {
        console.error("Error loading share token:", error);
        setTokenData(null);
        setTokenScope(null);
      }
    };

    loadToken();
  }, [shareToken, user]);

  // Load sharing group data when shareGroupToken is present
  useEffect(() => {
    if (!shareGroupToken) {
      setShareGroupData(null);
      setShareGroupScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareGroupToken) {
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadSharingGroup = async () => {
      try {
        const groupData = await getSharingGroupByToken(shareGroupToken);
        if (groupData) {
          setShareGroupData(groupData);
          // Calculate scope for the sharing group
          const scope = await calculateSharingGroupScope(groupData.id);
          setShareGroupScope(scope);
        } else {
          setShareGroupData(null);
          setShareGroupScope(null);
        }
      } catch (error) {
        console.error("Error loading sharing group:", error);
        setShareGroupData(null);
        setShareGroupScope(null);
      }
    };

    loadSharingGroup();
  }, [shareGroupToken, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name || "");
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId, user]);

  // Fetch runs for guest share link
  useEffect(() => {
    if (!isGuestShare || !user || !user.isAnonymous) return;

    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      const runsList = Array.from(runsMap.values());
      setRuns(runsList);
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach(docSnap => {
          runsMap.set(docSnap.id, {
            id: docSnap.id,
            ...docSnap.data()
          });
        });
        upsertRuns();
      }, (error) => {
        console.error("Error fetching shared runs:", error);
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, isGuestShare, shareGroupId, user]);

  // Fetch runs for token-based sharing
  useEffect(() => {
    if (!isTokenShare || !tokenScope || !user) return;

    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      // Filter runs by token scope
      const accessibleRunIds = new Set(tokenScope.runs || []);
      const filteredRuns = Array.from(runsMap.values()).filter(run => 
        accessibleRunIds.has(run.id)
      );
      setRuns(filteredRuns);
    };

    // Fetch all runs that might be in scope
    const runsRef = collection(db, "events");
    const unsubscribe = onSnapshot(runsRef, (snapshot) => {
      snapshot.docs.forEach(docSnap => {
        const runData = { id: docSnap.id, ...docSnap.data() };
        runsMap.set(docSnap.id, runData);
      });
      upsertRuns();
    }, (error) => {
      console.error("Error fetching runs for token:", error);
    });
    unsubscribers.push(unsubscribe);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isTokenShare, tokenScope, user]);

  // Fetch runs for sharing group token
  useEffect(() => {
    if (!isShareGroupShare || !shareGroupScope || !user) return;

    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      // Filter runs by sharing group scope
      const accessibleRunIds = new Set(shareGroupScope.runs || []);
      const filteredRuns = Array.from(runsMap.values()).filter(run => 
        accessibleRunIds.has(run.id)
      );
      setRuns(filteredRuns);
    };

    // Fetch all runs that might be in scope
    const runsRef = collection(db, "events");
    const unsubscribe = onSnapshot(runsRef, (snapshot) => {
      snapshot.docs.forEach(docSnap => {
        const runData = { id: docSnap.id, ...docSnap.data() };
        runsMap.set(docSnap.id, runData);
      });
      upsertRuns();
    }, (error) => {
      console.error("Error fetching runs for sharing group:", error);
    });
    unsubscribers.push(unsubscribe);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isShareGroupShare, shareGroupScope, user]);

  // Fetch runs for logged-in user
  useEffect(() => {
    if (!user || user.isAnonymous || isGuestShare || isTokenShare || isShareGroupShare) return;
    
    // TODO: After Firebase migration, change 'events' to 'runs'
    const runsRef = collection(db, "events");
    
    // Query 1: Runs where user is the owner (userId)
    const q1 = query(runsRef, where("userId", "==", user.uid));
    
    // Query 2: Runs where user is in admins array
    const q2 = query(runsRef, where("admins", "array-contains", user.uid));
    
    // Store runs from each query separately
    const runsFromUserId = new Map();
    const runsFromAdmins = new Map();
    
    // Function to merge and deduplicate runs
    const mergeAndUpdateRuns = () => {
      // Combine both maps - Map automatically handles deduplication by key
      const allRunsMap = new Map();
      
      // Add runs from userId query
      runsFromUserId.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      
      // Add runs from admins query (will overwrite duplicates with same ID)
      runsFromAdmins.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      
      // Convert to array, sort, and update state
      const mergedRuns = Array.from(allRunsMap.values());
      setRuns(mergedRuns);
    };
    
    // Subscribe to userId query
    const unsubscribe1 = onSnapshot(q1, (snapshot) => {
      runsFromUserId.clear();
      snapshot.docs.forEach(docSnap => {
        runsFromUserId.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      mergeAndUpdateRuns();
    }, (error) => {
      console.error("Error fetching runs by userId:", error);
    });
    
    // Subscribe to admins query
    const unsubscribe2 = onSnapshot(q2, (snapshot) => {
      runsFromAdmins.clear();
      snapshot.docs.forEach(docSnap => {
        runsFromAdmins.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      mergeAndUpdateRuns();
    }, (error) => {
      console.error("Error fetching runs by admins:", error);
    });
    
    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [isGuestShare, user]);

  // Fetch sessions
  useEffect(() => {
    if (!user) return;

    // For guests without tokens, don't show any sessions
    if (isGuest && !isGuestShare && !isTokenShare && !isShareGroupShare) {
      setSessions(new Map());
      return;
    }

    const sessionsRef = collection(db, "sessions");
    const q = query(sessionsRef, orderBy("date", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const sessionsMap = new Map();
      
      // Determine accessible run IDs based on sharing mode
      let accessibleRunIds = null;
      if (isTokenShare && tokenScope) {
        accessibleRunIds = new Set(tokenScope.runs || []);
      } else if (isShareGroupShare && shareGroupScope) {
        accessibleRunIds = new Set(shareGroupScope.runs || []);
      } else if (isGuestShare) {
        // For old shareGroupId system, use the runs we've already fetched
        accessibleRunIds = new Set(runs.map(r => r.id));
      }

      snapshot.docs.forEach(docSnap => {
        const sessionData = {
          id: docSnap.id,
          ...docSnap.data()
        };

        // For guests with tokens, only include sessions that contain accessible runs
        if (accessibleRunIds !== null) {
          const sessionRunIds = sessionData.runIds || sessionData.eventIds || [];
          const hasAccessibleRuns = sessionRunIds.some(runId => accessibleRunIds.has(runId));
          if (hasAccessibleRuns) {
            sessionsMap.set(docSnap.id, sessionData);
          }
        } else {
          // For logged-in users, show all sessions
          sessionsMap.set(docSnap.id, sessionData);
        }
      });
      
      setSessions(sessionsMap);
    }, (error) => {
      console.error("Error fetching sessions:", error);
    });

    return () => unsubscribe();
  }, [user, isGuest, isGuestShare, isTokenShare, isShareGroupShare, tokenScope, shareGroupScope, runs]);

  // Get session objects for a run
  const getRunSessionObjects = (run) => {
    const sessionIds = run.sessionIds || (run.sessionId ? [run.sessionId] : []);
    return sessionIds
      .map(id => sessions.get(id))
      .filter(Boolean);
  };

  // Get session names for a run (for display)
  const getRunSessions = (run) => {
    const sessionObjects = getRunSessionObjects(run);
    return sessionObjects.map(s => s.name).join(", ") || "—";
  };

  // Get all unique tracks from runs
  const availableTracks = useMemo(() => {
    const tracks = new Set();
    runs.forEach(run => {
      if (run.track && run.track.trim()) {
        tracks.add(run.track.trim());
      }
    });
    return Array.from(tracks).sort();
  }, [runs]);

  // Filter runs by session and track (multi-select)
  const filteredRuns = useMemo(() => {
    let filtered = runs;

    // Filter by sessions (if any selected)
    if (selectedSessionFilters.length > 0) {
      filtered = filtered.filter(run => {
        const runSessionIds = run.sessionIds || (run.sessionId ? [run.sessionId] : []);
        return runSessionIds.some(sessionId => selectedSessionFilters.includes(sessionId));
      });
    }

    // Filter by tracks (if any selected)
    if (selectedTrackFilters.length > 0) {
      filtered = filtered.filter(run => {
        const runTrack = run.track ? run.track.trim() : '';
        return runTrack && selectedTrackFilters.includes(runTrack);
      });
    }

    return filtered;
  }, [runs, selectedSessionFilters, selectedTrackFilters]);

  // Sort runs based on sortConfig
  const sortedRuns = useMemo(() => {
    const sorted = [...filteredRuns];
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt || 0;
          bValue = b.createdAt || 0;
          break;
        case 'session':
          const aSessions = getRunSessions(a);
          const bSessions = getRunSessions(b);
          aValue = aSessions.toLowerCase();
          bValue = bSessions.toLowerCase();
          break;
        case 'track':
          aValue = (a.track || '').toLowerCase();
          bValue = (b.track || '').toLowerCase();
          break;
        case 'racers':
          aValue = (a.racers || []).length;
          bValue = (b.racers || []).length;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredRuns, sessions, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort style={{ opacity: 0.3, marginLeft: '5px' }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp style={{ marginLeft: '5px' }} />
      : <FaSortDown style={{ marginLeft: '5px' }} />;
  };

  const handleUpdateRunName = async (runId, newName) => {
    if (!requireAccount()) return;
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await updateDoc(doc(db, "events", runId), { name: newName });
      setEditingRunId(null);
      showToast("Run name updated successfully.");
    } catch (error) {
      console.error("Error updating run name:", error);
      showToast("Failed to update run name.");
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!requireAccount()) return;
    const runToDelete = runs.find(r => r.id === runId);
    const runName = runToDelete ? runToDelete.name : 'this run';
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${runName}"?\n\nThis action cannot be undone and will permanently remove the run and all its data.`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await deleteDoc(doc(db, "events", runId));
      showToast("Run deleted successfully.");
    } catch (error) {
      console.error("Error deleting run", error);
      showToast("Failed to delete run. Please try again.");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString();
  };

  // Create new session
  const handleCreateSession = async () => {
    if (!requireAccount()) return;
    if (!newSessionName.trim()) {
      showToast("Please enter a session name.");
      return;
    }

    try {
      const sessionData = {
        name: newSessionName.trim(),
        date: new Date().toISOString().split('T')[0],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        userId: user.uid,
        runIds: [],
        runCount: 0
      };

      await addDoc(collection(db, "sessions"), sessionData);
      setNewSessionName("");
      setShowNewSessionInput(false);
      showToast("Session created successfully.");
    } catch (error) {
      console.error("Error creating session:", error);
      showToast("Failed to create session.");
    }
  };

  // Rename session
  const handleRenameSession = async (sessionId) => {
    if (!requireAccount()) return;
    if (!editingSessionName.trim()) {
      setEditingSessionId(null);
      return;
    }

    try {
      await updateDoc(doc(db, "sessions", sessionId), {
        name: editingSessionName.trim(),
        updatedAt: Date.now()
      });
      setEditingSessionId(null);
      setEditingSessionName("");
      showToast("Session renamed successfully.");
    } catch (error) {
      console.error("Error renaming session:", error);
      showToast("Failed to rename session.");
    }
  };

  // Assign run to session via drag & drop
  const handleDropOnSession = async (sessionId, runId) => {
    if (!requireAccount()) return;
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      const runRef = doc(db, "events", runId);
      const run = runs.find(r => r.id === runId);
      
      if (!run) return;

      const currentSessionIds = run.sessionIds || (run.sessionId ? [run.sessionId] : []);
      
      // Add sessionId if not already present
      if (!currentSessionIds.includes(sessionId)) {
        await updateDoc(runRef, {
          sessionIds: arrayUnion(sessionId)
        });

        // Update session's runIds (keeping eventIds for backward compatibility during migration)
        const sessionRef = doc(db, "sessions", sessionId);
        const session = sessions.get(sessionId);
        const runIds = session?.runIds || session?.eventIds || [];
        
        if (!runIds.includes(runId)) {
          await updateDoc(sessionRef, {
            runIds: arrayUnion(runId),
            eventIds: arrayUnion(runId), // Keep for backward compatibility
            runCount: (session?.runCount || session?.eventCount || 0) + 1,
            eventCount: (session?.runCount || session?.eventCount || 0) + 1, // Keep for backward compatibility
            updatedAt: Date.now()
          });
        }

        showToast("Run assigned to session successfully.");
      }
    } catch (error) {
      console.error("Error assigning run to session:", error);
      showToast("Failed to assign run to session.");
    }
  };

  // Remove run from session
  const handleRemoveRunFromSession = async (sessionId, runId) => {
    if (!requireAccount()) return;
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      const runRef = doc(db, "events", runId);
      await updateDoc(runRef, {
        sessionIds: arrayRemove(sessionId)
      });

      // Update session's runIds (keeping eventIds for backward compatibility during migration)
      const sessionRef = doc(db, "sessions", sessionId);
      const session = sessions.get(sessionId);
      const runIds = session?.runIds || session?.eventIds || [];
      
      if (runIds.includes(runId)) {
        const updatedRunIds = runIds.filter(id => id !== runId);
        await updateDoc(sessionRef, {
          runIds: updatedRunIds,
          eventIds: updatedRunIds, // Keep for backward compatibility
          runCount: updatedRunIds.length,
          eventCount: updatedRunIds.length, // Keep for backward compatibility
          updatedAt: Date.now()
        });
      }

      showToast("Run removed from session successfully.");
    } catch (error) {
      console.error("Error removing run from session:", error);
      showToast("Failed to remove run from session.");
    }
  };

  // Update run track
  const handleUpdateRunTrack = async (runId, track) => {
    if (!requireAccount()) return;
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await updateDoc(doc(db, "events", runId), { track: track.trim() || null });
      setEditingRunTrack(null);
      setRunTrackEdit("");
      showToast("Run track updated successfully.");
    } catch (error) {
      console.error("Error updating run track:", error);
      showToast("Failed to update run track.");
    }
  };

  // Drag handlers
  const handleDragStart = (runId) => {
    setDraggedRunId(runId);
  };

  const handleDragEnd = () => {
    setDraggedRunId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (sessionId) => {
    if (draggedRunId) {
      handleDropOnSession(sessionId, draggedRunId);
      setDraggedRunId(null);
    }
  };

  // Get sessions sorted by name
  const sortedSessions = useMemo(() => {
    return Array.from(sessions.values()).sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [sessions]);

  // Restore persisted filters when user changes
  useLayoutEffect(() => {
    if (isGuestShare) {
      setSelectedSessionFilters([]);
      setSelectedTrackFilters([]);
      setFiltersRestored(true);
      return;
    }
    setFiltersRestored(false);
    try {
      const raw = localStorage.getItem(getFiltersStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.selectedSessionFilters)) {
        setSelectedSessionFilters(parsed.selectedSessionFilters);
      }
      if (Array.isArray(parsed?.selectedTrackFilters)) {
        setSelectedTrackFilters(parsed.selectedTrackFilters);
      }
    } catch (error) {
      console.error("Failed to restore runs list filters:", error);
    }
    setFiltersRestored(true);
  }, [user?.uid]);

  // Persist filters between visits
  useEffect(() => {
    if (!filtersRestored || isGuestShare) return;
    try {
      const payload = {
        selectedSessionFilters,
        selectedTrackFilters,
      };
      localStorage.setItem(getFiltersStorageKey(), JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to persist runs list filters:", error);
    }
  }, [selectedSessionFilters, selectedTrackFilters, user?.uid]);

  // Filter dropdown open/close is handled by the shared FilterDropdown component.

  // Toggle session filter
  const toggleSessionFilter = (sessionId) => {
    setSelectedSessionFilters(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  // Toggle track filter
  const toggleTrackFilter = (track) => {
    setSelectedTrackFilters(prev => {
      if (prev.includes(track)) {
        return prev.filter(t => t !== track);
      } else {
        return [...prev, track];
      }
    });
  };

  // Clear all session filters
  const clearSessionFilters = () => {
    setSelectedSessionFilters([]);
  };

  // Clear all track filters
  const clearTrackFilters = () => {
    setSelectedTrackFilters([]);
  };

  // Get sessions to display (max 10, or all if showAllSessions is true)
  const MAX_SESSIONS_DISPLAY = 10;
  const displayedSessions = useMemo(() => {
    if (showAllSessions || sortedSessions.length <= MAX_SESSIONS_DISPLAY) {
      return sortedSessions;
    }
    return sortedSessions.slice(0, MAX_SESSIONS_DISPLAY);
  }, [sortedSessions, showAllSessions]);

  return (
    <div style={{ width: "100%", padding: "30px 20px", boxSizing: "border-box", maxWidth: "100%" }}>
      {/* Main content */}
      <div>
        <div className="homepage-other-tools">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h1 style={{ margin: 0 }}>Runs List</h1>
            {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {selectedRuns.size > 0 && (
                  <span style={{ fontSize: "0.9em", color: "#666" }}>
                    {selectedRuns.size} run{selectedRuns.size !== 1 ? "s" : ""} selected
                  </span>
                )}
                <button
                  onClick={() => {
                    if (selectedRuns.size === 0) {
                      showToast("Please select at least one run to share.");
                      return;
                    }
                    setShowShareModal(true);
                  }}
                  disabled={selectedRuns.size === 0}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: selectedRuns.size > 0 ? "#13A1A8" : "#ccc",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: selectedRuns.size > 0 ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "medium"
                  }}
                >
                  <FaShareAlt /> Share ({selectedRuns.size})
                </button>
                {selectedRuns.size > 0 && (
                  <button
                    onClick={() => setSelectedRuns(new Set())}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#999",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Sessions/Groups Section */}
          <div style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <h2 style={{ margin: 0, fontSize: "1.2em", fontWeight: "500" }}>Sessions/Groups</h2>
            </div>
            
            {showNewSessionInput && (
              <div style={{ 
                display: "flex", 
                gap: "10px", 
                marginBottom: "15px",
                padding: "12px",
                backgroundColor: "#f9f9f9",
                borderRadius: "4px"
              }}>
                <input
                  type="text"
                  placeholder="Enter session name..."
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateSession();
                    }
                    if (e.key === "Escape") {
                      setShowNewSessionInput(false);
                      setNewSessionName("");
                    }
                  }}
                  style={{ 
                    flex: 1, 
                    padding: "8px 12px", 
                    border: "1px solid #ddd", 
                    borderRadius: "4px",
                    fontSize: "0.95em"
                  }}
                  autoFocus
                />
                <button
                  onClick={handleCreateSession}
                  className="primary-btn"
                  style={{ padding: "8px 16px" }}
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewSessionInput(false);
                    setNewSessionName("");
                  }}
                  className="primary-btn"
                  style={{ padding: "8px 16px" }}
                >
                  <FaTimes />
                </button>
              </div>
            )}

            <div style={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: "8px",
              marginBottom: "20px",
              alignItems: "center"
            }}>
              {displayedSessions.map((session) => (
                <div
                  key={session.id}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(session.id)}
                  onClick={() => {
                    if (editingSessionId !== session.id) {
                      toggleSessionFilter(session.id);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "20px",
                    backgroundColor: "#f9f9f9",
                    transition: "all 0.2s",
                    cursor: "pointer",
                    fontSize: "0.9em"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#13A1A8";
                    e.currentTarget.style.backgroundColor = "#f0f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#ddd";
                    e.currentTarget.style.backgroundColor = "#f9f9f9";
                  }}
                  title={`Drop runs here (${session.runCount || session.eventCount || 0} run(s))`}
                >
                  {editingSessionId === session.id ? (
                    <>
                      <input
                        type="text"
                        value={editingSessionName}
                        onChange={(e) => setEditingSessionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleRenameSession(session.id);
                          }
                          if (e.key === "Escape") {
                            setEditingSessionId(null);
                            setEditingSessionName("");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          padding: "2px 6px", 
                          border: "1px solid #13A1A8", 
                          borderRadius: "12px",
                          fontSize: "0.9em",
                          width: "150px"
                        }}
                        autoFocus
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRenameSession(session.id);
                        }}
                        style={{
                          background: "#13A1A8",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7em",
                          padding: 0
                        }}
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSessionId(null);
                          setEditingSessionName("");
                        }}
                        style={{
                          background: "#999",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7em",
                          padding: 0
                        }}
                        title="Cancel"
                      >
                        <FaTimes />
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionFilter(session.id);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {session.name || "Unnamed Session"}
                      </span>
                      <span style={{ color: "#999", fontSize: "0.85em" }}>
                        ({session.runCount || session.eventCount || 0})
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSessionId(session.id);
                          setEditingSessionName(session.name || "");
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#666",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "0.8em"
                        }}
                        title="Rename session"
                      >
                        <FaPencilAlt />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* Show more/less button if there are more than MAX_SESSIONS_DISPLAY */}
              {sortedSessions.length > MAX_SESSIONS_DISPLAY && (
                <div
                  onClick={() => setShowAllSessions(!showAllSessions)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "20px",
                    backgroundColor: "#f9f9f9",
                    transition: "all 0.2s",
                    cursor: "pointer",
                    fontSize: "0.9em",
                    color: "#666"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#13A1A8";
                    e.currentTarget.style.backgroundColor = "#f0f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#ddd";
                    e.currentTarget.style.backgroundColor = "#f9f9f9";
                  }}
                  title={showAllSessions ? "Show less" : `Show all (${sortedSessions.length} total)`}
                >
                  {showAllSessions ? "—" : "⋯"}
                </div>
              )}

              {/* New Session button as a small capsule */}
              {!showNewSessionInput && (
                <div
                  onClick={() => {
                    if (!user || user.isAnonymous) {
                      setShowLoginRequiredModal(true);
                      return;
                    }
                    setShowNewSessionInput(true);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 12px",
                    border: "1px dashed #13A1A8",
                    borderRadius: "20px",
                    backgroundColor: "#f0f9fa",
                    transition: "all 0.2s",
                    cursor: "pointer",
                    fontSize: "0.9em",
                    color: "#13A1A8",
                    gap: "6px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#13A1A8";
                    e.currentTarget.style.backgroundColor = "#e0f4f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#13A1A8";
                    e.currentTarget.style.backgroundColor = "#f0f9fa";
                  }}
                  title="Create new session"
                >
                  <FaPlus style={{ fontSize: "0.8em" }} />
                  <span>New</span>
                </div>
              )}
            </div>
          </div>

          {/* Filter Status Indicators */}
          {(selectedSessionFilters.length > 0 || selectedTrackFilters.length > 0) && (
            <div style={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              {selectedSessionFilters.length > 0 && (
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px",
                  padding: "6px 12px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "16px",
                  fontSize: "0.9em"
                }}>
                  <span style={{ fontWeight: "500" }}>
                    Sessions: {selectedSessionFilters.length} selected
                  </span>
                  <button
                    onClick={clearSessionFilters}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#1976d2",
                      fontSize: "0.9em",
                      padding: "2px 6px"
                    }}
                    title="Clear session filters"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              {selectedTrackFilters.length > 0 && (
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px",
                  padding: "6px 12px",
                  backgroundColor: "#f3e5f5",
                  borderRadius: "16px",
                  fontSize: "0.9em"
                }}>
                  <span style={{ fontWeight: "500" }}>
                    Tracks: {selectedTrackFilters.length} selected
                  </span>
                  <button
                    onClick={clearTrackFilters}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#7b1fa2",
                      fontSize: "0.9em",
                      padding: "2px 6px"
                    }}
                    title="Clear track filters"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          )}
          
          {runs.length > 0 ? (
            <div style={{ 
              overflowX: "auto",
              border: "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: "#fff"
            }}>
              <table style={{ 
                width: "100%", 
                borderCollapse: "collapse",
                minWidth: "800px"
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: "#f5f5f5",
                    borderBottom: "2px solid #ddd"
                  }}>
                    {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
                      <th style={{ 
                        padding: "12px 8px", 
                        textAlign: "center",
                        width: "50px"
                      }}>
                        <input
                          type="checkbox"
                          checked={sortedRuns.length > 0 && selectedRuns.size === sortedRuns.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRuns(new Set(sortedRuns.map(r => r.id)));
                            } else {
                              setSelectedRuns(new Set());
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </th>
                    )}
                    <th 
                      style={{ 
                        padding: "12px 16px", 
                        textAlign: "left", 
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: "600",
                        fontSize: "0.9em",
                        color: "#333"
                      }}
                      onClick={() => handleSort('name')}
                    >
                      Run Name {getSortIcon('name')}
                    </th>
                    <th 
                      data-filter-column
                      style={{ 
                        padding: "12px 16px", 
                        textAlign: "left", 
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: "600",
                        fontSize: "0.9em",
                        color: "#333",
                        position: "relative"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span onClick={(e) => {
                          e.stopPropagation();
                          handleSort('session');
                        }} style={{ flex: 1 }}>
                          Session(s) {getSortIcon('session')}
                        </span>
                        <FilterDropdown
                          title="Filter by session"
                          dropdownTitle="Filter Sessions"
                          searchPlaceholder="Search sessions..."
                          emptyText="No sessions available"
                          options={sortedSessions}
                          selectedValues={selectedSessionFilters}
                          getOptionValue={(session) => session.id}
                          getOptionLabel={(session) => session.name || "Unnamed Session"}
                          renderOptionSuffix={(session) => (
                            <span style={{ color: "#999", marginLeft: "8px", fontSize: "0.85em" }}>
                              ({session.runCount || session.eventCount || 0})
                            </span>
                          )}
                          onToggle={toggleSessionFilter}
                          onClear={clearSessionFilters}
                          buttonText="Filter"
                          activeBackgroundColor="#1976d2"
                          inactiveTextColor="#666"
                          isOpen={openFilterDropdown === "session"}
                          onOpenChange={(open) => setOpenFilterDropdown(open ? "session" : null)}
                          enableSearch
                        />
                      </div>
                    </th>
                    <th 
                      data-filter-column
                      style={{ 
                        padding: "12px 16px", 
                        textAlign: "left", 
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: "600",
                        fontSize: "0.9em",
                        color: "#333",
                        position: "relative"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span onClick={(e) => {
                          e.stopPropagation();
                          handleSort('track');
                        }} style={{ flex: 1 }}>
                          Track {getSortIcon('track')}
                        </span>
                        <FilterDropdown
                          title="Filter by track"
                          dropdownTitle="Filter Tracks"
                          searchPlaceholder="Search tracks..."
                          emptyText="No tracks available"
                          options={availableTracks}
                          selectedValues={selectedTrackFilters}
                          onToggle={toggleTrackFilter}
                          onClear={clearTrackFilters}
                          buttonText="Filter"
                          activeBackgroundColor="#7b1fa2"
                          inactiveTextColor="#666"
                          isOpen={openFilterDropdown === "track"}
                          onOpenChange={(open) => setOpenFilterDropdown(open ? "track" : null)}
                          enableSearch
                        />
                      </div>
                    </th>
                    <th 
                      style={{ 
                        padding: "12px 16px", 
                        textAlign: "left", 
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: "600",
                        fontSize: "0.9em",
                        color: "#333"
                      }}
                      onClick={() => handleSort('createdAt')}
                    >
                      Created {getSortIcon('createdAt')}
                    </th>
                    <th 
                      style={{ 
                        padding: "12px 16px", 
                        textAlign: "center", 
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: "600",
                        fontSize: "0.9em",
                        color: "#333"
                      }}
                      onClick={() => handleSort('racers')}
                    >
                      Racers {getSortIcon('racers')}
                    </th>
                    <th style={{ 
                      padding: "12px 16px", 
                      textAlign: "center",
                      fontWeight: "600",
                      fontSize: "0.9em",
                      color: "#333"
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRuns.map((run, index) => (
                    <tr
                      key={run.id}
                      draggable
                      onDragStart={() => handleDragStart(run.id)}
                      onDragEnd={handleDragEnd}
                      style={{
                        ...(selectedRuns.has(run.id) ? { backgroundColor: "#e3f2fd" } : {}),
                        borderBottom: index < sortedRuns.length - 1 ? "1px solid #eee" : "none",
                        transition: "background-color 0.2s",
                        cursor: "move",
                        opacity: draggedRunId === run.id ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = selectedRuns.has(run.id) ? "#e3f2fd" : "#f9f9f9"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedRuns.has(run.id) ? "#e3f2fd" : "transparent"}
                    >
                      {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
                        <td 
                          style={{ padding: "12px 8px", textAlign: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRuns.has(run.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newSelected = new Set(selectedRuns);
                              if (e.target.checked) {
                                newSelected.add(run.id);
                              } else {
                                newSelected.delete(run.id);
                              }
                              setSelectedRuns(newSelected);
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                      )}
                      <td style={{ padding: "12px 16px" }}>
                        {editingRunId === run.id ? (
                          <input
                            type="text"
                            value={runNameEdit}
                            onChange={(e) => setRunNameEdit(e.target.value)}
                            onBlur={() => {
                              if (runNameEdit.trim() && runNameEdit !== run.name) {
                                handleUpdateRunName(run.id, runNameEdit.trim());
                              } else {
                                setEditingRunId(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (runNameEdit.trim() && runNameEdit !== run.name) {
                                  handleUpdateRunName(run.id, runNameEdit.trim());
                                } else {
                                  setEditingRunId(null);
                                }
                              }
                              if (e.key === "Escape") {
                                setEditingRunId(null);
                              }
                            }}
                            style={{ 
                              fontSize: "1em", 
                              padding: "6px 8px", 
                              width: "100%",
                              border: "1px solid #13A1A8",
                              borderRadius: "3px"
                            }}
                            autoFocus
                          />
                        ) : (
                          <div>
                            <strong 
                              style={{ 
                                fontSize: "1em", 
                                display: "block", 
                                marginBottom: "4px",
                                cursor: "pointer",
                                color: "#13A1A8",
                                textDecoration: "underline"
                              }}
                              onClick={() => navigate(`/timer?runId=${run.id}`)}
                              title="Click to view run"
                            >
                              {run.name || "Untitled Run"}
                            </strong>
                            <small style={{ color: "#888", fontSize: "0.85em" }}>
                              {run.racers ? (
                                <>
                                  Finished: {run.racers.filter(r => r.startTime && r.finishTime).length} / 
                                  Total: {run.racers.length}, 
                                  On Course: {run.racers.filter(r => r.startTime && !r.finishTime).length}
                                </>
                              ) : (
                                "No racers"
                              )}
                            </small>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#666" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {getRunSessionObjects(run).map((session) => (
                            <span
                              key={session.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 8px",
                                backgroundColor: "#13A1A820",
                                color: "#13A1A8",
                                borderRadius: "12px",
                                fontSize: "0.85em"
                              }}
                            >
                              {session.name}
                              {run.userId === user.uid && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveRunFromSession(session.id, run.id);
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    marginLeft: "4px",
                                    color: "#13A1A8"
                                  }}
                                  title="Remove from session"
                                >
                                  <FaTimes style={{ fontSize: "0.75em" }} />
                                </button>
                              )}
                            </span>
                          ))}
                          {getRunSessionObjects(run).length === 0 && <span>—</span>}
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#666" }}>
                        {editingRunTrack === run.id ? (
                          <input
                            type="text"
                            value={runTrackEdit}
                            onChange={(e) => setRunTrackEdit(e.target.value)}
                            onBlur={() => {
                              if (runTrackEdit !== (run.track || "")) {
                                handleUpdateRunTrack(run.id, runTrackEdit);
                              } else {
                                setEditingRunTrack(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (runTrackEdit !== (run.track || "")) {
                                  handleUpdateRunTrack(run.id, runTrackEdit);
                                } else {
                                  setEditingRunTrack(null);
                                }
                              }
                              if (e.key === "Escape") {
                                setEditingRunTrack(null);
                                setRunTrackEdit("");
                              }
                            }}
                            style={{ 
                              fontSize: "0.9em", 
                              padding: "4px 8px", 
                              width: "100%",
                              border: "1px solid #13A1A8",
                              borderRadius: "3px"
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => {
                              setEditingRunTrack(run.id);
                              setRunTrackEdit(run.track || "");
                            }}
                            style={{ 
                              cursor: "pointer",
                              textDecoration: run.track ? "none" : "underline",
                              color: run.track ? "#666" : "#999"
                            }}
                            title="Click to edit track"
                          >
                            {run.track || "Click to add track"}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#666" }}>
                        {formatDate(run.createdAt)}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center", fontSize: "0.9em", color: "#666" }}>
                        {run.racers ? run.racers.length : 0}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                          {run.userId === user.uid && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingRunId(run.id);
                                  setRunNameEdit(run.name);
                                }}
                                className="primary-btn"
                                title="Edit Run Name"
                                style={{ padding: "6px 10px", fontSize: "0.85em" }}
                              >
                                <FaPencilAlt />
                              </button>
                              <button
                                onClick={() => handleDeleteRun(run.id)}
                                className="primary-btn"
                                title="Delete Run"
                                style={{ padding: "6px 10px", fontSize: "0.85em" }}
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => navigate(`/timer?runId=${run.id}`)}
                            className="primary-btn"
                            style={{ padding: "6px 12px", fontSize: "0.85em" }}
                          >
                            Select
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>You have no runs yet.</p>
          )}
          
          <button 
            className="primary-btn" 
            onClick={() => {
              if (!user || user.isAnonymous) {
                setShowLoginRequiredModal(true);
                return;
              }
              navigate("/timer");
            }}
            style={{ marginTop: "20px" }}
          >
            Create New Run
          </button>
        </div>
      </div>

      {/* Share Group Modal */}
      <ShareGroupModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedRuns(new Set());
        }}
        resources={Array.from(selectedRuns).map(runId => {
          const run = sortedRuns.find(r => r.id === runId);
          return run ? {
            type: 'run',
            id: run.id,
            name: run.name || `Run ${run.id}`
          } : null;
        }).filter(Boolean)}
        onShareComplete={() => {
          setShowShareModal(false);
          setSelectedRuns(new Set());
        }}
      />

      {/* Login Required Modal */}
      {showLoginRequiredModal && (
        <div className="modal-overlay" onClick={() => setShowLoginRequiredModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0 }}>Login Required</h2>
              <button
                onClick={() => setShowLoginRequiredModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "#666",
                }}
              >
                <FaTimes />
              </button>
            </div>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              You need to be logged in to create a run. Please create an account or sign in to continue.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowLoginRequiredModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#999",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLoginRequiredModal(false);
                  navigate("/login?signup=true");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#13A1A8",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunsList;
